import React, { useState, useEffect, useCallback } from 'react';
import { receiptService, GetReceiptsParams } from '../../../apiServices/receiptService';
import { Receipt, ReceiptListItem, TransactionType, ReceiptStatus } from '../../../apiServices/types';
import { useNotification } from '../../../contexts/NotificationContext';
import { useRefresh } from '../../../contexts/RefreshContext';
import { formatCurrency } from '../../../utils';
import ReceiptDetailsModal from '../../ReceiptDetailsModal/ReceiptDetailsModal';
import { GlassSelect } from '../../UI';
import './HistoryPage.css';

const HistoryPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [receipts, setReceipts] = useState<ReceiptListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'Completed' | 'InProgress'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'Expense' | 'Income'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getDateRange = useCallback((filter: 'all' | 'today' | 'week' | 'month') => {
    if (filter === 'all') return { dateFrom: undefined, dateTo: undefined };
    
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    switch (filter) {
      case 'today':
        return {
          dateFrom: todayStart.toISOString(),
          dateTo: todayEnd.toISOString()
        };
      case 'week': {
        const dayOfWeek = todayStart.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const mondayStart = new Date(todayStart);
        mondayStart.setDate(todayStart.getDate() - daysToMonday);
        mondayStart.setHours(0, 0, 0, 0);
        return {
          dateFrom: mondayStart.toISOString(),
          dateTo: todayEnd.toISOString()
        };
      }
      case 'month': {
        const monthStart = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
        return {
          dateFrom: monthStart.toISOString(),
          dateTo: todayEnd.toISOString()
        };
      }
      default:
        return { dateFrom: undefined, dateTo: undefined };
    }
  }, []);

  const loadReceipts = useCallback(async (reset: boolean = false, currentPageNumber?: number) => {
    try {
      if (reset) {
        setLoading(true);
        setReceipts([]);
        setHasMore(true);
        setPageNumber(1);
      } else {
        if (loadingMore) return;
        setLoadingMore(true);
      }
      
      const pageToLoad = reset ? 1 : (currentPageNumber ?? pageNumber);
      const dateRange = getDateRange(dateFilter);
      const params: GetReceiptsParams = {
        onlyDeleted: showDeleted ? true : false,
        search: debouncedSearchTerm || undefined,
        status: statusFilter !== 'all' ? (statusFilter === 'Completed' ? ReceiptStatus.Completed : ReceiptStatus.InProgress) : undefined,
        type: typeFilter !== 'all' ? (typeFilter === 'Income' ? TransactionType.Income : TransactionType.Expense) : undefined,
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        pageNumber: pageToLoad,
        pageSize
      };

      const result = await receiptService.getAllPaged(params);
      
      if (reset) {
        setReceipts(result.data);
        setPageNumber(2);
      } else {
        setReceipts(prev => [...prev, ...result.data]);
        setPageNumber(prev => prev + 1);
      }
      
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setHasMore(result.pageNumber < result.totalPages);
    } catch (error) {
      showNotification('Помилка завантаження чеків', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [showDeleted, debouncedSearchTerm, statusFilter, typeFilter, dateFilter, pageNumber, pageSize, getDateRange, showNotification, loadingMore]);

  useEffect(() => {
    loadReceipts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted, debouncedSearchTerm, statusFilter, typeFilter, dateFilter]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore || loading) return;

      // Знаходимо scrollable контейнер .content-area
      const scrollContainer = document.querySelector('.content-area');
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadReceipts(false, pageNumber);
      }
    };

    const scrollContainer = document.querySelector('.content-area');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [loadReceipts, loadingMore, hasMore, loading, pageNumber]);

  const handleRefresh = useCallback(async () => {
    await loadReceipts(true);
  }, [loadReceipts]);

  useRefresh(handleRefresh);

  const toTypeName = (type: unknown): 'Expense' | 'Income' => {
    if (typeof type === 'string') {
      return type === 'Income' ? 'Income' : 'Expense';
    }
    return Number(type) === TransactionType.Income ? 'Income' : 'Expense';
  };

  const isIncomeType = (type: unknown): boolean => toTypeName(type) === 'Income';

  const toStatusName = (status: unknown): 'Completed' | 'InProgress' => {
    if (typeof status === 'string') {
      return status === 'Completed' ? 'Completed' : 'InProgress';
    }
    return Number(status) === ReceiptStatus.Completed ? 'Completed' : 'InProgress';
  };

  const isCompletedStatus = (status: unknown): boolean => toStatusName(status) === 'Completed';


  // Тихе оновлення даних без показу loading стану
  const silentRefresh = useCallback(async () => {
    try {
      const dateRange = getDateRange(dateFilter);
      const params: GetReceiptsParams = {
        onlyDeleted: showDeleted ? true : false,
        search: debouncedSearchTerm || undefined,
        status: statusFilter !== 'all' ? (statusFilter === 'Completed' ? ReceiptStatus.Completed : ReceiptStatus.InProgress) : undefined,
        type: typeFilter !== 'all' ? (typeFilter === 'Income' ? TransactionType.Income : TransactionType.Expense) : undefined,
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        pageNumber: 1,
        pageSize: Math.max(receipts.length, pageSize) // Завантажуємо всі поточні елементи
      };

      const result = await receiptService.getAllPaged(params);
      setReceipts(result.data);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setHasMore(result.pageNumber < result.totalPages);
      setPageNumber(Math.ceil(result.data.length / pageSize) + 1);
    } catch (error) {
      // Ігноруємо помилки при тихому оновленні
    }
  }, [showDeleted, debouncedSearchTerm, statusFilter, typeFilter, dateFilter, receipts.length, pageSize, getDateRange]);

  const handleReceiptClick = async (receipt: ReceiptListItem) => {
    try {
      setLoadingModal(true);
      const receiptDetails = await receiptService.getById(receipt.id);
      setSelectedReceipt(receiptDetails);
      setShowModal(true);
    } catch (error) {
      showNotification('Помилка завантаження деталей чека', 'error');
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReceipt(null);
    setLoadingModal(false);
    silentRefresh();
  };

  const getStatusColor = (status: unknown) => {
    return isCompletedStatus(status) ? 'status-completed' : 'status-in-progress';
  };

  const getTypeColor = (type: unknown) => {
    return isIncomeType(type) ? 'type-income' : 'type-expense';
  };
  
  const getAmountTypeColor = (type: unknown) => {
    return isIncomeType(type) ? 'amount-income' : 'amount-expense';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setDateFilter('all');
  };

  return (
    <div className="history-page">
      <div className="card">
        <div className="card-header-with-toggle">
          <div>
            <h2 className="card-title">Історія чеків</h2>
            <p className="card-subtitle">
              {showDeleted ? 'Архів видалених чеків' : 'Перегляд усіх транзакцій'} • {totalCount} чеків
            </p>
          </div>
          <button
            className={`toggle-view-btn ${showDeleted ? 'active' : ''}`}
            onClick={() => setShowDeleted(!showDeleted)}
            title={showDeleted ? 'Показати активні чеки' : 'Показати видалені чеки'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {showDeleted ? (
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              ) : (
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              )}
            </svg>
            {showDeleted ? 'Показати активні' : 'Показати архів'}
          </button>
        </div>
        
        <div className="filters-section">
          <div className="filters-header">
            <div className="search-wrapper">
              <div className="search-box">
                <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  placeholder="Пошук по назві або рахунку..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="clear-search"
                    aria-label="Очистити пошук"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div
              className="filters-toggle"
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
            >
              <span className="filters-label">Фільтри</span>
              <svg 
                className={`filters-toggle-icon ${filtersCollapsed ? '' : 'expanded'}`}
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>

          <div className={`filters-group ${filtersCollapsed ? 'collapsed' : ''}`}>
            <div className="filters-row">
              <div className="filter-item">
                <GlassSelect
                  options={[
                    { value: 'all', label: 'Всі статуси' },
                    { value: 'Completed', label: 'Завершені' },
                    { value: 'InProgress', label: 'В процесі' }
                  ]}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as 'all' | 'Completed' | 'InProgress')}
                  placeholder="Статус"
                />
              </div>

              <div className="filter-item">
                <GlassSelect
                  options={[
                    { value: 'all', label: 'Всі типи' },
                    { value: 'Expense', label: 'Витрати' },
                    { value: 'Income', label: 'Доходи' }
                  ]}
                  value={typeFilter}
                  onChange={(value) => setTypeFilter(value as 'all' | 'Expense' | 'Income')}
                  placeholder="Тип"
                />
              </div>

              <div className="filter-item">
                <GlassSelect
                  options={[
                    { value: 'all', label: 'Весь час' },
                    { value: 'today', label: 'Сьогодні' },
                    { value: 'week', label: 'Останній тиждень' },
                    { value: 'month', label: 'Останній місяць' }
                  ]}
                  value={dateFilter}
                  onChange={(value) => setDateFilter(value as 'all' | 'today' | 'week' | 'month')}
                  placeholder="Період"
                />
              </div>
            </div>
            
            {(statusFilter !== 'all' || typeFilter !== 'all' || dateFilter !== 'all' || searchTerm) && (
              <button 
                onClick={clearFilters} 
                className="btn-clear-filters"
                aria-label="Очистити всі фільтри"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Очистити
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="receipts-list">
        {loading ? (
          <div className="card">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Завантаження історії...</p>
            </div>
          </div>
        ) : receipts.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-icon">📋</div>
            <div className="empty-title">
              {totalCount === 0 ? 'Немає чеків' : 'Немає результатів'}
            </div>
            <div className="empty-text">
              {totalCount === 0 
                ? 'Створіть свій перший чек на сторінці витрат або доходів'
                : 'Спробуйте змінити фільтри або пошуковий запит'
              }
            </div>
          </div>
        ) : (
          <>
            {receipts.map((receipt) => (
            <div
              key={receipt.id}
              className={`card receipt-item ${loadingModal ? 'disabled' : ''}`}
              onClick={() => !loadingModal && handleReceiptClick(receipt)}
              style={{ cursor: loadingModal ? 'not-allowed' : 'pointer', opacity: loadingModal ? 0.6 : 1 }}
            >
              <div className="receipt-main">
                <div className="receipt-info">
                  <h3 className="receipt-name">{receipt.name}</h3>
                  <p className="receipt-account">{receipt.accountName}</p>
                  <p className="receipt-date">{formatDate(receipt.date)}</p>
                </div>
                <div className="receipt-amount">
                  <span className={`amount ${getAmountTypeColor(receipt.type)}`}>
                    {isIncomeType(receipt.type) ? '+' : '-'}
                    {formatCurrency(receipt.totalAmount)}
                  </span>
                </div>
              </div>
              <div className="receipt-meta">
                <span className={`status ${getStatusColor(receipt.status)}`}>
                  {isCompletedStatus(receipt.status) ? 'Завершено' : 'В процесі'}
                </span>
                <span className={`type ${getTypeColor(receipt.type)}`}>
                  {isIncomeType(receipt.type) ? 'Дохід' : 'Витрата'}
                </span>
                {receipt.isDeleted && (
                  <span className="status status-deleted">
                    Видалено
                  </span>
                )}
                <span className="positions-count">
                  {receipt.positionsCount} позицій
                </span>
              </div>
            </div>
          ))}
          
          {loadingMore && (
            <div className="loading-more">
              <div className="spinner"></div>
              <p>Завантаження...</p>
            </div>
          )}
          
          {!hasMore && receipts.length > 0 && (
            <div className="end-of-list">
              <p>Всі чеки завантажено</p>
            </div>
          )}
          </>
        )}
      </div>

      {loadingModal && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження деталей чека...</p>
          </div>
        </div>
      )}

      {showModal && selectedReceipt && (
        <ReceiptDetailsModal
          receipt={selectedReceipt}
          onClose={handleCloseModal}
          onUpdate={loadReceipts}
          onReceiptUpdate={(updatedReceipt) => setSelectedReceipt(updatedReceipt)}
        />
      )}
    </div>
  );
};

export default HistoryPage;
